<script setup lang="ts">
import { onMounted } from 'vue';

import { WIDGET_LAUNCHER_SIZE } from '@support/widget-contracts';

import { hasUnreadReply as hasUnread, refreshUnread } from '@/api/unread';
import { usePolling } from '@/composables/usePolling';

defineEmits<{
  open: [];
}>();

/**
 * Slow, because nobody is looking at a closed launcher the way they look at an
 * open thread: half a minute late on a dot costs nothing, and two requests a
 * minute is what a visitor with the panel closed should cost at most.
 */
const UNREAD_INTERVAL_MS = 30_000;

usePolling(refreshUnread, UNREAD_INTERVAL_MS);

onMounted(() => {
  // The launcher mounts when the panel closes — exactly the moment to ask once
  // without waiting out an interval. A failure leaves no dot, which is the
  // honest state of "do not know".
  void refreshUnread().catch(() => undefined);
});
</script>

<template>
  <button
    data-testid="widget-launcher"
    type="button"
    class="launcher"
    :aria-label="hasUnread ? 'Open support — new reply' : 'Open support'"
    :style="{ width: `${WIDGET_LAUNCHER_SIZE}px`, height: `${WIDGET_LAUNCHER_SIZE}px` }"
    @click="$emit('open')"
  >
    <!-- Inline SVG, no icon library: one glyph does not justify a dependency
         that every visitor downloads. -->
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>

    <span
      v-if="hasUnread"
      data-testid="widget-launcher-unread"
      class="launcher__unread"
    />
  </button>
</template>

<style scoped>
.launcher {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--w-accent);
  color: var(--w-accent-text);
  box-shadow: var(--w-shadow);
  transition: transform 120ms ease, filter 120ms ease;
}

.launcher:hover {
  filter: brightness(1.08);
}

.launcher:active {
  transform: scale(0.96);
}

/* Inside the circle's box, not hanging off it: the frame is exactly the size of
   the launcher, so anything past its edge is clipped by the host container. */
.launcher__unread {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 14px;
  height: 14px;
  border: 2px solid var(--w-bg);
  border-radius: 50%;
  background: var(--w-danger);
}
</style>
